import {
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  Children,
  cloneElement,
  isValidElement,
} from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import { IoIosArrowDown, IoIosArrowForward, IoIosClose, IoIosSearch } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { toAppPath, getInternalPathFromLocation } from "../../lib/appNavigation";
import useAbacAccess from "../../hooks/useAbacAccess";
import useModal from "../../hooks/useModal";
import {
  ROUTING_ADD_ACCESS_PATHS,
  ROUTING_EDIT_ACCESS_PATHS,
} from "../../components/User/Abac/abacAccess";
import { SMS_MESSAGE_VIEW_PATHS } from "../../components/User/Abac/abacRouteMap";
import { AppIcons } from "../../assets/icon/appIcons";
import {
  authSelector,
  clearLogoutAuthState,
  getNavOptionsData,
  logoutUser,
} from "../../components/Auth/AuthSlice";
import {
  toggleBulkSMSOverlay,
  toggleQuickSMSOverlay,
} from "../../components/Message/MessageSlice";
import { useFetch } from "../../hooks/useFetch";
import {
  checkForWalletMin,
  clearAuthStorage,
  getItem,
  checkUserRole,
  hasRoles,
  isArray,
  isManager,
  isSeller,
  isUser,
  someTrue,
  storeItem,
  showToast,
} from "../../lib/utility";
import { middleware, persistor, resetAppState } from "../../redux/store";
import "./Sidebar.scss";
import { getSmscNames } from "../../components/User/UserSlice";
import { resolveSidebarAvatarSrc } from "../../lib/profileImage";
import { profileSelector } from "../../components/Profile/ProfileSlice";
import {
  walletSelector,
} from "../../components/Wallet/WalletSlice";
import { useLayoutChrome } from "../LayoutChromeContext";
import SidebarActivePill from "./SidebarActivePill";
import { useSidebarActivePill } from "./useSidebarActivePill";

const initialValue = {
  providers: false,
  campaign: false,
  summary: false,
  detailed: false,
  performance: false,
  view: false,
  user: false,
  errorCode: false,
  report: false,
  smsc: false,
  routing: false,
  sender: false,
  utilities: false,
};

/** Persist accordion section open/closed across refresh (cleared on logout via clearAuthStorage). */
const SIDEBAR_SECTIONS_KEY = "sidebarSectionsOpen";

function readSectionOpen(sectionId, fallback = false) {
  try {
    const saved = getItem(SIDEBAR_SECTIONS_KEY, true);
    if (
      saved &&
      typeof saved === "object" &&
      !Array.isArray(saved) &&
      typeof saved[sectionId] === "boolean"
    ) {
      return saved[sectionId];
    }
  } catch {
    /* ignore corrupt storage */
  }
  return fallback;
}

function persistSectionOpen(sectionId, nextOpen) {
  let saved = {};
  try {
    const prev = getItem(SIDEBAR_SECTIONS_KEY, true);
    if (prev && typeof prev === "object" && !Array.isArray(prev)) {
      saved = { ...prev };
    }
  } catch {
    saved = {};
  }
  saved[sectionId] = nextOpen;
  storeItem(SIDEBAR_SECTIONS_KEY, saved);
}

/** Sidebar avatar slot: optional dedicated URL, else same as login brand (`VITE_LOGIN_BRAND_LOGO_URL`). */
function getSidebarBrandImageUrl() {
  const sid = import.meta.env.VITE_SIDEBAR_BRAND_LOGO_URL;
  if (typeof sid === "string" && sid.trim() !== "") return sid.trim();
  const loginBrand = import.meta.env.VITE_LOGIN_BRAND_LOGO_URL;
  if (typeof loginBrand === "string" && loginBrand.trim() !== "")
    return loginBrand.trim();
  return "";
}

const SidebarContext = createContext();

function updateSidebarTooltipPosition(e) {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty(
    "--sidebar-tooltip-top",
    `${rect.top + rect.height / 2}px`,
  );
}

function getCollapsedTooltipProps(label, isCollapsed) {
  if (!isCollapsed || !label) return {};
  return {
    "data-tooltip": label,
    "data-tooltip-location": "right",
    onMouseEnter: updateSidebarTooltipPosition,
    onMouseMove: updateSidebarTooltipPosition,
    onFocus: updateSidebarTooltipPosition,
  };
}


function getLogicalPath(location) {
  const internal = getInternalPathFromLocation(location);
  return (
    (internal.split("?")[0] || "/")
      .replace(/\/{2,}/g, "/")
      .replace(/\/+$/, "") || "/"
  );
}

function isLogicalNavActive(to, location, activeMatch) {
  const current = getLogicalPath(location);
  if (typeof activeMatch === "function") return activeMatch(current);
  if (!to) return false;
  const target =
    ("/" + String(to).replace(/^\/+/, ""))
      .replace(/\/{2,}/g, "/")
      .replace(/\/+$/, "") || "/";
  return current === target;
}

/** Active matchers for sidebar parents that own in-page tabs */
const ACTIVE = {
  messages: (p) =>
    p === "/message" ||
    p.startsWith("/utilities/edit/bulk") ||
    p.startsWith("/utilities/edit/schedule"),
  users: (p) => p === "/user" || p === "/user/list" || p === "/user/limit",
  bulk: (p) => p.startsWith("/approvals/bulk"),
  senderId: (p) => p.startsWith("/approvals/sender"),
  errorCodes: (p) => p === "/user/error-code" || p === "/smsc/error-code",
  routing: (p) =>
    (p === "/routing" || p.startsWith("/routing/")) &&
    !p.startsWith("/routing/sender"),
  senderRouting: (p) => p.startsWith("/routing/sender"),
};

const rv = (...segments) => ["sms", "reports", ...segments, "view"];
const util = (...segments) => ["sms", "sms_utilities", ...segments];


const NAV_ABAC = {
  dashboard: ["sms", "dashboard", "view"],
  message: SMS_MESSAGE_VIEW_PATHS,
  campaignManager: util("campaign_manager", "view"),
  scheduleManager: util("schedule_manager", "view"),
  twoWay: util("two_way", "view"),
  subscription: ["sms", "sms_utilities", "subscription", "view"],
  characterConverter: ["sms", "sms_utilities", "character_converter", "view"],
  template: ["sms", "template", "view"],
  addressBook: ["address_book", "view"],
  firewall: ["sms", "firewall", "view"],
  company: ["company", "view"],
  vendor: ["vendor", "view"],
  sales: ["sales_team", "view"],
  errorCode: ["sms", "smsc_providers", "error_code", "view"],
  smscList: ["sms", "smsc_providers", "smsc", "view"],
  smscLooping: ["sms", "smsc_providers", "looping", "view"],
  smscBlocking: ["sms", "smsc_providers", "blocking", "view"],
  smscSchedule: ["sms", "smsc_providers", "schedule", "view"],
  smscSpecial: ["sms", "smsc_providers", "special_setting", "view"],
  smscGroup: ["sms", "smsc_providers", "group", "view"],
  smscAlert: ["sms", "smsc_providers", "alert", "view"],
  smscLimits: ["sms", "smsc_providers", "limits", "view"],
  smscHttp: ["sms", "smsc_providers", "http_smsc", "view"],
  smscHlr: ["sms", "smsc_providers", "hlr", "view"],
  userList: ["user", "profile", "view"],
  userLimit: ["user", "limit", "view"],
  routingAdd: ROUTING_ADD_ACCESS_PATHS,
  // *View = front entry; *Update = modify inside. apply/undo/previous are in-page only.
  routingEdit: ROUTING_EDIT_ACCESS_PATHS,
  routingCopy: ["sms", "routing", "copy", "view"],
  routingSenderAdd: ["sms", "routing", "sender", "add", "view"],
  routingSenderEdit: ["sms", "routing", "sender", "edit", "view"],
  bulkApprove: util("approvals", "bulk", "approve_reject", "view"),
  bulkLog: util("approvals", "bulk", "log", "view"),
  senderApprove: util("approvals", "sender", "approve_reject", "view"),
  senderLog: util("approvals", "sender", "log", "view"),
};
NAV_ABAC.utilities = [
  NAV_ABAC.campaignManager,
  NAV_ABAC.scheduleManager,
  NAV_ABAC.twoWay,
  NAV_ABAC.subscription,
];
NAV_ABAC.reportCampaign = [
  rv("campaign", "aborted"),
  rv("campaign", "campaign"),
  rv("campaign", "schedule"),
];
NAV_ABAC.reportSummary = [rv("summary", "delivery"), rv("summary", "batch")];
NAV_ABAC.reportDetailed = [
  rv("detailed_analytics", "delivery_advance"),
  rv("detailed_analytics", "delivery_content"),
  rv("detailed_analytics", "delivery_smsc"),
];
NAV_ABAC.reportPerformance = [
  rv("performance", "user"),
  rv("performance", "route"),
];
NAV_ABAC.reports = [
  ...NAV_ABAC.reportCampaign,
  ...NAV_ABAC.reportSummary,
  ...NAV_ABAC.reportDetailed,
  rv("delivery_latency"),
  rv("consumption"),
  rv("lookup"),
  rv("aggregator"),
  rv("profit"),
  ...NAV_ABAC.reportPerformance,
  rv("blocked"),
  rv("tps"),
  rv("vendor_summary"),
  rv("edr"),
  rv("coverage"),
];
NAV_ABAC.bulkApprovals = [NAV_ABAC.bulkApprove, NAV_ABAC.bulkLog];
NAV_ABAC.senderApprovals = [NAV_ABAC.senderApprove, NAV_ABAC.senderLog];
NAV_ABAC.approvals = [
  ...NAV_ABAC.bulkApprovals,
  ...NAV_ABAC.senderApprovals,
];
NAV_ABAC.userSuper = [NAV_ABAC.userList, NAV_ABAC.userLimit];
NAV_ABAC.routingSender = [
  NAV_ABAC.routingSenderAdd,
  NAV_ABAC.routingSenderEdit,
];
NAV_ABAC.routing = [
  NAV_ABAC.routingAdd,
  ...NAV_ABAC.routingEdit,
  NAV_ABAC.routingCopy,
  ...NAV_ABAC.routingSender,
];
NAV_ABAC.smscMenu = [
  NAV_ABAC.smscList,
  NAV_ABAC.smscLooping,
  NAV_ABAC.smscBlocking,
  NAV_ABAC.smscSchedule,
  NAV_ABAC.smscSpecial,
  NAV_ABAC.smscGroup,
];

const LinkItem = ({
  name,
  translationKey,
  icon,
  to,
  navOption,
  roleCondition,
  customValidation,
  abacPath,
  toggle,
  bypassSearch = false,
  activeMatch,
  children,
}) => {
  const { t } = useTranslation("sidebar");
  const location = useLocation();
  const {
    user,
    navOptions,
    submenusOpen,
    isSidebarCollapsed,
    menuSearchQuery,
    snapToElement,
    handleSubmenuToggleClick,
    closeSidebarIfMobile,
    canAbac,
    canAbacAny,
  } = useContext(SidebarContext);
  const displayName = translationKey ? t(translationKey) : name;
  const collapsedTooltipProps = getCollapsedTooltipProps(
    displayName,
    isSidebarCollapsed,
  );
  const isActive = isLogicalNavActive(to, location, activeMatch);
  const searchQ = (menuSearchQuery || "").trim().toLowerCase();
  const nameMatchesSearch =
    !searchQ || String(displayName || "").toLowerCase().includes(searchQ);
  const isParentMenu = !to && Boolean(children);
  // Parent title hit → show all children; else children filter themselves.
  const searchChildren =
    searchQ && isParentMenu && nameMatchesSearch
      ? Children.map(children, (child) =>
          isValidElement(child)
            ? cloneElement(child, { bypassSearch: true })
            : child,
        )
      : children;

  let shouldRender = navOption ? navOptions?.[navOption] : true;
    if (roleCondition !== undefined)
      shouldRender = shouldRender && hasRoles(roleCondition, user);
    if (customValidation !== undefined)
      shouldRender = shouldRender && customValidation;
    if (abacPath !== undefined && canAbac) {
      const isMulti = Array.isArray(abacPath[0]);
      shouldRender =
        shouldRender && (isMulti ? canAbacAny(abacPath) : canAbac(abacPath));
    }
    // Leaf items filter by search; parents stay mounted so children can match.
    if (searchQ && !isParentMenu && !bypassSearch) {
      shouldRender = shouldRender && nameMatchesSearch;
    }

    return (
      <>
        {shouldRender && (
          <>
            {to ? (
              isSidebarCollapsed ? (
                <div className="sidebar-link-tooltip-wrap" {...collapsedTooltipProps}>
                  <NavLink
                    to={toAppPath("/" + to)}
                    className={
                      "link collapsed" +
                      (icon ? " has-icon" : "") +
                      (isActive ? " active" : "")
                    }
                    onClick={(e) => {
                    snapToElement(e.currentTarget);
                    closeSidebarIfMobile?.();
                  }}
                  >
                    {icon && (
                      <img
                        loading="lazy"
                        src={icon}
                        alt=""
                        className="sidebar-link-icon"
                      />
                    )}
                    <span className="text-light-gray">{displayName}</span>
                  </NavLink>
                </div>
              ) : (
                <NavLink
                  to={toAppPath("/" + to)}
                  className={
                    "link collapsed" +
                    (icon ? " has-icon" : "") +
                    (isActive ? " active" : "")
                  }
                  onClick={(e) => {
                    snapToElement(e.currentTarget);
                    closeSidebarIfMobile?.();
                  }}
                >
                  {icon && (
                    <img
                      loading="lazy"
                      src={icon}
                      alt=""
                      className="sidebar-link-icon"
                    />
                  )}
                  <span className="text-light-gray">{displayName}</span>
                </NavLink>
              )
            ) : (
                children && (
                <div className="dropdown">
                  {isSidebarCollapsed ? (
                    <div
                      className="sidebar-link-tooltip-wrap"
                      {...collapsedTooltipProps}
                    >
                      <div
                        className={"link" + (icon ? " has-icon" : "")}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubmenuToggleClick(e, toggle);
                        }}
                      >
                        {icon && (
                          <img
                            src={icon}
                            alt=""
                            loading="lazy"
                            className="sidebar-link-icon"
                          />
                        )}
                        <span className="text-light-gray">{displayName}</span>
                        {submenusOpen[toggle] ? (
                          <IoIosArrowDown className="sidebar-submenu-caret is-open" />
                        ) : (
                          <IoIosArrowForward className="sidebar-submenu-caret" />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={"link" + (icon ? " has-icon" : "")}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmenuToggleClick(e, toggle);
                      }}
                    >
                      {icon && (
                        <img
                          src={icon}
                          alt=""
                          loading="lazy"
                          className="sidebar-link-icon"
                        />
                      )}
                      <span className="text-light-gray">{displayName}</span>
                      {submenusOpen[toggle] ? (
                        <IoIosArrowDown />
                      ) : (
                        <IoIosArrowForward />
                      )}
                    </div>
                  )}
                  {(submenusOpen[toggle] || Boolean(searchQ)) && (
                    <div className="submenu">{searchChildren}</div>
                  )}
                </div>
              )
            )}
          </>
        )}
      </>
    );
};

/** Accordion section: closed by default; open state survives refresh. */
const MenuSection = ({
  titleKey,
  sectionId,
  defaultOpen = false,
  icon,
  children,
}) => {
  const { t } = useTranslation("sidebar");
  const {
    isSidebarCollapsed,
    menuSearchQuery,
    refreshPill,
    clearIntent,
  } = useContext(SidebarContext);
  const [open, setOpen] = useState(() =>
    readSectionOpen(sectionId, defaultOpen),
  );
  const [hasVisibleChildren, setHasVisibleChildren] = useState(true);
  const bodyRef = useRef(null);
  const searchQ = (menuSearchQuery || "").trim();
  // Search expands all matching sections; collapsed rail keeps accordion toggle.
  const isOpen = Boolean(searchQ) || open;
  const sectionTitle = t(titleKey);

  const syncVisibility = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // Leaves always count. Parent dropdowns only count when they still have a
    // matching link (empty search shells must not keep the section header).
    const leaves = el.querySelectorAll(
      ":scope > a.link, :scope > .sidebar-link-tooltip-wrap",
    ).length;
    const filledDropdowns = Array.from(
      el.querySelectorAll(":scope > .dropdown"),
    ).filter((node) => Boolean(node.querySelector("a.link"))).length;
    setHasVisibleChildren(leaves + filledDropdowns > 0);
  }, []);

  useLayoutEffect(() => {
    // Always keep section mounted so clearing search can remount leaf links and
    // re-measure. Returning null previously stranded hasVisibleChildren=false.
    syncVisibility();
    const el = bodyRef.current;
    if (!el) return undefined;
    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => {
            // Defer one frame so React commit of LinkItem filters is in the DOM.
            requestAnimationFrame(syncVisibility);
          })
        : null;
    mo?.observe(el, { childList: true, subtree: true });
    // Extra pass after search text changes (LinkItems re-render async to paint).
    const t1 = setTimeout(syncVisibility, 0);
    const t2 = setTimeout(syncVisibility, 50);
    return () => {
      mo?.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [syncVisibility, menuSearchQuery, children]);

  const handleToggle = () => {
    if (searchQ) return;
    setOpen((prev) => {
      const next = !prev;
      persistSectionOpen(sectionId, next);
      if (prev) {
        clearIntent?.();
      }
      requestAnimationFrame(() => refreshPill?.());
      return next;
    });
  };

  return (
    <div
      className={
        "sidebar-section" +
        (isOpen ? " is-open" : "") +
        (isSidebarCollapsed ? " is-collapsed-rail" : "") +
        (!hasVisibleChildren ? " is-empty" : "")
      }
      data-section={sectionId}
      hidden={!hasVisibleChildren}
      aria-hidden={!hasVisibleChildren}
    >
      <button
        type="button"
        className="sidebar-section-header"
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-label={sectionTitle}
        title={isSidebarCollapsed ? sectionTitle : undefined}
        tabIndex={hasVisibleChildren ? 0 : -1}
      >
        <span className="sidebar-section-title">{sectionTitle}</span>
        <span className="sidebar-section-icon-row">
          {icon ? (
            <img
              src={icon}
              alt=""
              className="sidebar-section-icon"
              loading="lazy"
            />
          ) : null}
          {isOpen ? (
            <IoIosArrowDown className="sidebar-section-caret" />
          ) : (
            <IoIosArrowForward className="sidebar-section-caret" />
          )}
        </span>
      </button>
      <div
        ref={bodyRef}
        className="sidebar-section-body"
        hidden={!isOpen}
        aria-hidden={!isOpen}
      >
        {children}
      </div>
    </div>
  );
};

const Sidebar = () => {
  const { t } = useTranslation("sidebar");
  const { t: tNav } = useTranslation("navbar");
  const location = useLocation();
  const sidebarLinksRef = useRef(null);
  const submenuSnapParentsRef = useRef({});
  const submenuCloseTimersRef = useRef({});
  const layoutChrome = useLayoutChrome();
  const {
    clickRef: moreClickRef,
    contRef: moreContRef,
    showModal: showSidebarMore,
    handleOpen: openSidebarMore,
    handleClose: closeSidebarMore,
  } = useModal();

  const [submenusOpen, setSubmenusOpen] = useState(
    getItem("sideActive", true) || initialValue
  );
  const [shouldShowDlvSmsc, setShouldShowDlvSmsc] = useState(
    getItem("shouldShowDlvSmsc", true) ?? false
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => getItem("sidebarCollapsed", true) ?? false
  );
  const [menuSearchQuery, setMenuSearchQuery] = useState("");
  const [hasSearchHits, setHasSearchHits] = useState(true);
  const { pill, refreshPill, snapToElement, clearIntent } = useSidebarActivePill(
    sidebarLinksRef,
    [location.pathname, isSidebarCollapsed, submenusOpen, menuSearchQuery],
  );

  const isMenuSearching = Boolean((menuSearchQuery || "").trim());

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, navOptions } = useSelector(authSelector);
  const { balanceData: walletBalanceRaw, isSuccessBalance } =
    useSelector(walletSelector);
  const {
    canAccess: canAbac,
    canAccessAny: canAbacAny,
    canAccessNavbar,
    canAccessWalletUi,
    canAccessWalletPage,
    canViewPlatformDocuments,
    canViewSdkDocuments,
    canViewDashboard,
    canViewSmscProviders,
    canViewFirewall,
    shouldEnforce,
  } = useAbacAccess();
  const canViewProfile = canAccessNavbar("profile");
  const canShowQuickMessageNav =
    canAccessNavbar("quick_message") &&
    canAbac("sms", "message", "quick_send", "view");
  const canShowBulkMessageNav =
    canAccessNavbar("bulk_message") &&
    canAbac("sms", "message", "bulk_campaign", "view");
  const canShowSubmissionReportNav = canAccessNavbar("submission_report");
  const canViewNotifications = canAccessNavbar("notifications");
  const canViewDownloads = canAccessNavbar("downloads");
  const canCustomizePanel = canAccessNavbar("customize_panel");
  const { getProfile: storeProfile } = useSelector(profileSelector);
  const sidebarBrandImageUrl = getSidebarBrandImageUrl();
  const sidebarProfile =
    storeProfile && Object.keys(storeProfile).length > 0
      ? storeProfile
      : globalThis.profileData;
  const sidebarAvatar = resolveSidebarAvatarSrc(
    sidebarProfile,
    sidebarBrandImageUrl,
  );

  useLayoutEffect(() => {
    if (!isMenuSearching) {
      setHasSearchHits(true);
      return undefined;
    }
    const root = sidebarLinksRef.current;
    if (!root) {
      setHasSearchHits(false);
      return undefined;
    }
    const measure = () => {
      setHasSearchHits(root.querySelectorAll("a.link").length > 0);
    };
    measure();
    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => requestAnimationFrame(measure))
        : null;
    mo?.observe(root, { childList: true, subtree: true });
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 50);
    return () => {
      mo?.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isMenuSearching, menuSearchQuery, isSidebarCollapsed, user, navOptions]);

  const getNavOptionsDataOnce = () => {
    dispatch(getNavOptionsData()).then((action) =>
      middleware(action, "Nav Options")
    );
  };

  const toggleSubMenu = useCallback((submenu) => {
    setSubmenusOpen((prev) => {
      const newState = {
        ...prev,
        [submenu]: !prev[submenu],
      };
      storeItem("sideActive", newState);
      return newState;
    });
  }, []);

  const reanchorPillAfterSubmenuClose = useCallback(
    (toggleKey) => {
      const parentEl = submenuSnapParentsRef.current[toggleKey];
      submenuSnapParentsRef.current[toggleKey] = null;
      if (submenuCloseTimersRef.current[toggleKey]) {
        clearTimeout(submenuCloseTimersRef.current[toggleKey]);
        submenuCloseTimersRef.current[toggleKey] = null;
      }
      if (!parentEl) return;

      const stillActive = sidebarLinksRef.current?.querySelector("a.link.active");
      if (stillActive) {
        refreshPill();
      } else if (parentEl instanceof Element) {
        snapToElement(parentEl);
      } else {
        refreshPill();
      }
    },
    [refreshPill, snapToElement],
  );

  const handleSubmenuToggleClick = useCallback(
    (e, toggleKey) => {
      const willOpen = !submenusOpen[toggleKey];
      const clickedLink = e.currentTarget;

      if (willOpen) {
        if (submenuCloseTimersRef.current[toggleKey]) {
          clearTimeout(submenuCloseTimersRef.current[toggleKey]);
          submenuCloseTimersRef.current[toggleKey] = null;
        }
        submenuSnapParentsRef.current[toggleKey] = null;
        snapToElement(clickedLink);
        toggleSubMenu(toggleKey);
        requestAnimationFrame(() => refreshPill());
        return;
      }

      clearIntent();
      submenuSnapParentsRef.current[toggleKey] = clickedLink;
      toggleSubMenu(toggleKey);

      if (submenuCloseTimersRef.current[toggleKey]) {
        clearTimeout(submenuCloseTimersRef.current[toggleKey]);
      }
      submenuCloseTimersRef.current[toggleKey] = setTimeout(() => {
        reanchorPillAfterSubmenuClose(toggleKey);
      }, 260);
    },
    [
      clearIntent,
      reanchorPillAfterSubmenuClose,
      refreshPill,
      snapToElement,
      submenusOpen,
      toggleSubMenu,
    ],
  );

  useEffect(() => {
    const timers = submenuCloseTimersRef.current;
    return () => {
      Object.keys(timers).forEach((key) => {
        if (timers[key]) clearTimeout(timers[key]);
      });
    };
  }, []);

  const toggleSidebarCollapse = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const container = document.querySelector(".sidebarContainer");
    const isMobileDrawer = container?.classList.contains("show");
    if (isMobileDrawer) {
      container.classList.remove("show");
      return;
    }
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      storeItem("sidebarCollapsed", next);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    document.body.classList.toggle("sidebar-collapsed", isSidebarCollapsed);
    return () => document.body.classList.remove("sidebar-collapsed");
  }, [isSidebarCollapsed]);

  const logout = () => {
    const finalizeClientLogout = () => {
      dispatch(resetAppState());
      dispatch(clearLogoutAuthState());
      clearAuthStorage();
      persistor.purge();
      document.body.classList.remove("dark-mode");
      globalThis.profileData = null;

      window.location.replace(toAppPath("/login"));
    };

    dispatch(logoutUser()).then((action) => {
      if (!action.error) {
        showToast(action.payload?.message || "Logout successful", "success");
      } else {
        showToast("Session cleared locally. Please login again.", "warning");
      }

      finalizeClientLogout();
    });
  };

  const sidebarOptionOpen = () => {
    let obj = {};
    // Use logical path so /du/{token} still opens the correct submenu
    const internalPath = getInternalPathFromLocation(window.location);
    const pathname = internalPath.split("?")[0] || window.location.pathname;
    Object.keys(submenusOpen).forEach((submenu) => {
      if (
        pathname.includes("/user/error-code") ||
        pathname.includes("/smsc/error-code")
      ) {
        obj[submenu] = submenu === "errorCode";
      } else if (submenu === "providers") {
        obj[submenu] = pathname.includes("/smsc");
      } else if (submenu === "detailed") {
        obj[submenu] = pathname.includes("/detailed");
      } else {
        obj[submenu] = pathname.includes(submenu);
      }
    });
    return obj;
  };

  const routingLandingPath = hasRoles("tenant,superadmin,system", user)
    ? "routing/add"
    : "routing/edit";

  const getSmscList = () => {
    dispatch(getSmscNames()).then((action) =>
      middleware(action, "Message", (action) => {
        if (isArray(action.payload.data)) {
          setShouldShowDlvSmsc(action.payload.data.length > 0);
          storeItem("shouldShowDlvSmsc", action.payload.data.length > 0);
        }
      })
    );
  };

  useEffect(() => {
    setTimeout(() => {
      let activeElemet = document.querySelector(".link.active");
      activeElemet &&
        activeElemet.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 500);
  }, []);

  useFetch(() => {
    if (hasRoles("admin", user)) getSmscList(); 
    setSubmenusOpen(sidebarOptionOpen()); 

    if (!isManager(user) && !isSeller(user)) {
      getNavOptionsDataOnce(); 
    }
  });
  const getSidebarContainer = () =>
    document.querySelector(".sidebarContainer");

  const isMobileView = () => {
    return (
      document.body.dataset.size === "mobile" ||
      document.body.dataset.size === "tab" ||
      window.innerWidth <= 1024
    );
  };

  const closeSidebarIfMobile = () => {
    if (isMobileView()) {
      getSidebarContainer()?.classList.remove("show");
    }
  };

  useEffect(() => {
    closeSidebarIfMobile();
  }, [location.pathname]);

  const closeSidebar = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    getSidebarContainer()?.classList.remove("show");
  };

  const toggleSidebar = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const container = getSidebarContainer();
    if (!container) return;
    const willOpen = !container.classList.contains("show");
    container.classList.toggle("show");
    // Mobile drawer needs full labels/search — don't keep desktop-collapsed rail
    if (willOpen && isMobileView() && isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
      storeItem("sidebarCollapsed", false);
    }
  };

  const handleSidebarBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeSidebar(e);
    }
  };

  return (
    <SidebarContext.Provider
      value={{
        user,
        navOptions,
        submenusOpen,
        toggleSubMenu,
        isSidebarCollapsed,
        menuSearchQuery,
        snapToElement,
        clearIntent,
        refreshPill,
        handleSubmenuToggleClick,
        closeSidebarIfMobile,
        canAbac,
        canAbacAny,
      }}
    >
      <div className="sidebarContainer" onClick={handleSidebarBackdropClick}>
        <button
          type="button"
          className="sidebarCollapseBtn"
          onClick={(e) => {
            e.stopPropagation();
            toggleSidebarCollapse(e);
          }}
          aria-label={
            isSidebarCollapsed
              ? t("tooltips.expandSidebar", { defaultValue: "Expand Sidebar" })
              : t("tooltips.collapseSidebar", { defaultValue: "Collapse Sidebar" })
          }
          data-tooltip={
            isSidebarCollapsed
              ? t("tooltips.expandSidebar", { defaultValue: "Expand Sidebar" })
              : t("tooltips.collapseSidebar", { defaultValue: "Collapse Sidebar" })
          }
          data-tooltip-location="right"
        >
          <img
            className="sidebarCollapseBtn-collapseIcon"
            src={AppIcons.collapse}
            alt=""
            loading="lazy"
          />
          <IoIosClose
            className="sidebarCollapseBtn-closeIcon"
            aria-hidden="true"
          />
        </button>
        <div
          className={
            "sidebar" +
            ((menuSearchQuery || "").trim() ? " is-searching" : "")
          }
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sidebar-top-card">
            <div className="sidebar-profile-row">
              <div
                className="profileHeader"
                onClick={() => {
                  if (!canViewProfile) return;
                  navigate(toAppPath("/profile"));
                  closeSidebarIfMobile();
                }}
                {...getCollapsedTooltipProps(
                  globalThis.profileData?.userName ?? "Profile",
                  isSidebarCollapsed,
                )}
              >
                <img
                  key={sidebarAvatar.src}
                  src={sidebarAvatar.src}
                  alt=""
                  className={
                    "userImage prevent-dark-icon" +
                    (sidebarAvatar.variant === "brand"
                      ? " sidebar-brand-logo"
                      : "")
                  }
                  loading="lazy"
                />
                {!isSeller(user) && !isManager(user) ? (
                  <div>
                    <p
                      className="fs-18 fw-600 text-gray mb-0 lh-1 userNameDetail-name"
                      title={`${globalThis.profileData?.userName}`}
                    >
                      {globalThis.profileData?.userName ?? "N/A"}
                    </p>
                    <p className="fs-12 fw-600 text-light-gray mb-0">
                      {globalThis.profileData?.roles || "N/A"}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p
                      className="fs-18 fw-600 text-gray mb-0 lh-1 userNameDetail-name"
                      title={globalThis.profileData?.userName || "N/A"}
                    >
                      {globalThis.profileData?.userName || "N/A"}
                    </p>
                    <p className="fs-12 fw-600 text-light-gray mb-0">
                      {globalThis.profileData?.role || "N/A"}
                    </p>
                  </div>
                )}
              </div>
              <div className="sidebar-more-wrap" ref={moreClickRef}>
                <button
                  type="button"
                  className="sidebar-more-btn"
                  aria-label={tNav("tooltips.more", { defaultValue: "More" })}
                  aria-expanded={showSidebarMore}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showSidebarMore) closeSidebarMore();
                    else openSidebarMore();
                  }}
                >
                  <BsThreeDotsVertical size={18} />
                </button>
                {showSidebarMore && (
                  <div
                    className="sidebar-more-menu"
                    ref={moreContRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {canViewNotifications && (
                      <button
                        type="button"
                        className="sidebar-more-item"
                        onClick={() => {
                          closeSidebarMore();
                          layoutChrome?.call("openNotifications");
                        }}
                      >
                        <img src={AppIcons.notification} alt="" loading="lazy" />
                        <span>
                          {tNav("tooltips.notifications", {
                            defaultValue: "Notification",
                          })}
                        </span>
                      </button>
                    )}
                    {!isSeller(user) &&
                      !isManager(user) &&
                      canShowQuickMessageNav && (
                        <button
                          type="button"
                          className="sidebar-more-item"
                          onClick={() => {
                            closeSidebarMore();
                            closeSidebarIfMobile();
                            if (layoutChrome?.call) {
                              layoutChrome.call("openQuickMessage");
                            } else {
                              navigate("/message");
                              dispatch(toggleQuickSMSOverlay(true));
                            }
                          }}
                        >
                          <img
                            src={AppIcons.quickMessage}
                            alt=""
                            loading="lazy"
                          />
                          <span>
                            {tNav("tooltips.quickMessage", {
                              defaultValue: "Quick Message",
                            })}
                          </span>
                        </button>
                      )}
                    {!isSeller(user) &&
                      !isManager(user) &&
                      canShowBulkMessageNav && (
                        <button
                          type="button"
                          className="sidebar-more-item"
                          onClick={() => {
                            closeSidebarMore();
                            closeSidebarIfMobile();
                            if (layoutChrome?.call) {
                              layoutChrome.call("openBulkMessage");
                            } else {
                              navigate("/message");
                              dispatch(toggleBulkSMSOverlay(true));
                            }
                          }}
                        >
                          <img
                            src={AppIcons.bulkMessage}
                            alt=""
                            loading="lazy"
                          />
                          <span>
                            {tNav("tooltips.bulkMessage", {
                              defaultValue: "Bulk Message",
                            })}
                          </span>
                        </button>
                      )}
                    {canViewDownloads && (
                      <button
                        type="button"
                        className="sidebar-more-item"
                        onClick={() => {
                          closeSidebarMore();
                          layoutChrome?.call("openDownloads");
                        }}
                      >
                        <img
                          src={AppIcons.fileDownloadProgress}
                          alt=""
                          loading="lazy"
                        />
                        <span>
                          {tNav("tooltips.fileDownloadProgress", {
                            defaultValue: "file download progress",
                          })}
                        </span>
                      </button>
                    )}
                    {!isSeller(user) &&
                      !isManager(user) &&
                      canShowSubmissionReportNav && (
                        <button
                          type="button"
                          className="sidebar-more-item"
                          onClick={() => {
                            closeSidebarMore();
                            layoutChrome?.call("openSubmissionReport");
                          }}
                        >
                          <img
                            src={AppIcons.submissionReport}
                            alt=""
                            loading="lazy"
                          />
                          <span>
                            {tNav("tooltips.submissionReport", {
                              defaultValue: "Submission Report",
                            })}
                          </span>
                        </button>
                      )}
                    {hasRoles("admin", user) && canCustomizePanel && (
                      <button
                        type="button"
                        className="sidebar-more-item"
                        onClick={() => {
                          closeSidebarMore();
                          layoutChrome?.call("openCustomizePanel");
                        }}
                      >
                        <img
                          src={AppIcons.advancedSettings}
                          alt=""
                          loading="lazy"
                        />
                        <span>
                          {tNav("tooltips.customizePanel", {
                            defaultValue: "Customize Panel",
                          })}
                        </span>
                      </button>
                    )}
                    {!isSeller(user) &&
                      !isManager(user) &&
                      canAccessWalletUi &&
                      (() => {
                        const data =
                          walletBalanceRaw?.data ?? walletBalanceRaw ?? {};
                        const credits = data.credits;
                        const wallets =
                          data.walletBalance ?? data.wallets ?? data.balance;
                        const currency = data.currency || "";
                        const walletFlag =
                          (
                            data.walletType ||
                            (data.walletBalance != null || data.wallets != null
                              ? "wallet"
                              : "")
                          )?.toLowerCase() === "wallet"
                            ? "yes"
                            : "no";
                        const isWalletEnabled = checkForWalletMin(walletFlag);
                        const isEnhanced = hasRoles(
                          "tenant,superadmin,system",
                          user,
                        );
                        const fmt = (v) =>
                          v === null || v === undefined ? "0" : String(v);
                        const rows = [];
                        if (isEnhanced || !isWalletEnabled) {
                          rows.push({
                            key: "crd",
                            amount: fmt(credits),
                            currency: "CRD",
                          });
                        }
                        if (isEnhanced || isWalletEnabled) {
                          rows.push({
                            key: "wallet",
                            amount: fmt(wallets),
                            currency: currency || "",
                          });
                        }
                        if (!rows.length && isSuccessBalance) {
                          rows.push({
                            key: "crd",
                            amount: fmt(credits),
                            currency: "CRD",
                          });
                        }
                        return rows.map((row) => (
                          <button
                            key={row.key}
                            type="button"
                            className="sidebar-more-item sidebar-more-item--wallet"
                            onClick={() => {
                              closeSidebarMore();
                              if (!canAccessWalletPage) return;
                              if (layoutChrome?.call) {
                                layoutChrome.call("goToWallet");
                              } else {
                                navigate("/wallet");
                              }
                              closeSidebarIfMobile();
                            }}
                          >
                            <img
                              src={AppIcons.crdEur}
                              alt=""
                              loading="lazy"
                            />
                            <span>
                              {row.amount}
                              {row.currency ? ` ${row.currency}` : ""}
                            </span>
                          </button>
                        ));
                      })()}
                  </div>
                )}
              </div>
            </div>
            {!isSidebarCollapsed && (
              <div className="sidebar-search">
                <IoIosSearch className="sidebar-search-icon" aria-hidden="true" />
                <input
                  type="text"
                  className="sidebar-search-input"
                  value={menuSearchQuery}
                  onChange={(e) => setMenuSearchQuery(e.target.value)}
                  placeholder={t("search.placeholder", { defaultValue: "Search" })}
                  aria-label={t("search.placeholder", { defaultValue: "Search" })}
                />
                {Boolean((menuSearchQuery || "").trim()) && (
                  <button
                    type="button"
                    className="sidebar-search-clear"
                    onClick={() => setMenuSearchQuery("")}
                    aria-label={t("search.clear", { defaultValue: "Clear search" })}
                    title={t("search.clear", { defaultValue: "Clear search" })}
                  >
                    <IoIosClose aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
            <div className="sidebar-dashboard-slot">
              {!(isSeller(user) || isManager(user)) ? (
                <LinkItem
                  translationKey="menu.dashboard"
                  to={"dashboard"}
                  abacPath={NAV_ABAC.dashboard}
                  customValidation={canViewDashboard}
                  icon={AppIcons.dashboard}
                  bypassSearch
                />
              ) : (
                <LinkItem
                  translationKey="menu.dashboard"
                  to={"dashboard"}
                  customValidation={canViewDashboard}
                  icon={AppIcons.dashboard}
                  bypassSearch
                />
              )}
            </div>
          </div>
          {!(isSeller(user) || isManager(user)) ? (
            <div className="links sidebar-nav" ref={sidebarLinksRef}>
              <SidebarActivePill pill={pill} />

              <MenuSection
                titleKey="sections.customerEngagement"
                sectionId="customerEngagement"
                icon={AppIcons.sectionEngagement}
                defaultOpen
              >
                <LinkItem translationKey="menu.messages" to={"message"} abacPath={NAV_ABAC.message} icon={AppIcons.messages} navOption={"sms"} activeMatch={ACTIVE.messages} />
                <LinkItem translationKey="menu.templates" to={"template"} abacPath={NAV_ABAC.template} icon={AppIcons.templates} navOption={"template"} />
                <LinkItem translationKey="menu.twoWayMessaging" to={"utilities/two-way"} abacPath={NAV_ABAC.twoWay} navOption={"twoWay"} icon={AppIcons.twoWayMessaging} />
                <LinkItem translationKey="menu.contacts" to={"address-book"} abacPath={NAV_ABAC.addressBook} icon={AppIcons.contacts} navOption={"addBook"} />
                <LinkItem translationKey="menu.optInPages" to={"utilities/subscription"} abacPath={NAV_ABAC.subscription} icon={AppIcons.optInPages} />
              </MenuSection>

              <MenuSection
                titleKey="sections.vendorsProviders"
                sectionId="vendorsProviders"
                icon={AppIcons.sectionVendorProviders}
              >
                <LinkItem translationKey="menu.vendors" roleCondition={"superadmin,system,tenant"} icon={AppIcons.vendors} to={"vendor"} abacPath={NAV_ABAC.vendor} />
                <LinkItem translationKey="menu.providers" icon={AppIcons.providers} roleCondition={"superadmin,system,tenant"} toggle={"providers"} abacPath={NAV_ABAC.smscMenu} customValidation={canViewSmscProviders}>
                  <LinkItem translationKey="menu.smscConnections" to={"smsc/list"} icon={AppIcons.smscConnections} abacPath={NAV_ABAC.smscList} />
                  <LinkItem translationKey="menu.httpConnections" to={"smsc/http"} icon={AppIcons.httpConnections} roleCondition={"superadmin,system"} abacPath={NAV_ABAC.smscHttp} />
                  <LinkItem translationKey="menu.groupConnections" to={"smsc/group-data"} icon={AppIcons.groupConnections} abacPath={NAV_ABAC.smscGroup} />
                  <LinkItem translationKey="menu.hlrConnection" to={"smsc/hlr"} icon={AppIcons.hlrConnection} roleCondition={"superadmin,system"} abacPath={NAV_ABAC.smscHlr} />
                  <LinkItem translationKey="menu.alerts" to={"smsc/alert"} icon={AppIcons.alerts} customValidation={!checkUserRole(user, "tenant")} abacPath={NAV_ABAC.smscAlert} />
                  <LinkItem translationKey="menu.trafficLimits" to={"smsc/limits"} icon={AppIcons.trafficLimits} customValidation={!checkUserRole(user, "tenant")} abacPath={NAV_ABAC.smscLimits} />
                  <LinkItem translationKey="menu.looping" to={"smsc/looping"} icon={AppIcons.looping} abacPath={NAV_ABAC.smscLooping} />
                  <LinkItem translationKey="menu.trafficBlocking" to={"smsc/blocking"} icon={AppIcons.trafficBlocking} abacPath={NAV_ABAC.smscBlocking} />
                  <LinkItem translationKey="reports.schedule" to={"smsc/schedule"} icon={AppIcons.schedule} abacPath={NAV_ABAC.smscSchedule} />
                  <LinkItem translationKey="menu.advancedSettings" to={"smsc/special-settings"} icon={AppIcons.advancedSettings} abacPath={NAV_ABAC.smscSpecial} />
                </LinkItem>
              </MenuSection>

              <MenuSection
                titleKey="sections.organizationAccounts"
                sectionId="organizationAccounts"
                icon={AppIcons.sectionOrganizationAccounts}
              >
                <LinkItem
                  translationKey="menu.organizations"
                  roleCondition={"superadmin,system,admin,tenant,seller,manager"}
                  icon={AppIcons.organizations}
                  to={"company"}
                  abacPath={
                    isSeller(user) || isManager(user)
                      ? undefined
                      : NAV_ABAC.company
                  }
                />
                <LinkItem translationKey="menu.users" to={"user"} abacPath={NAV_ABAC.userList} icon={AppIcons.users} navOption={"user"} roleCondition={"admin,tenant,superadmin,system"} activeMatch={ACTIVE.users} />
                <LinkItem translationKey="menu.resellers" to={"sales"} abacPath={NAV_ABAC.sales} icon={AppIcons.resellers} navOption={"sales"} roleCondition={"admin,tenant,superadmin,system"} />
              </MenuSection>

              <MenuSection
                titleKey="sections.messageOperations"
                sectionId="messageOperations"
                icon={AppIcons.sectionMessageOperations}
              >
                {/* DLT — hidden from sidebar (components/routes remain; do not show in nav) */}
                <LinkItem
                  translationKey="menu.bulkManagement"
                  to={"approvals/bulk/approved-reject"}
                  icon={AppIcons.bulkManagement}
                  abacPath={NAV_ABAC.bulkApprovals}
                  roleCondition={"superadmin,system"}
                  activeMatch={ACTIVE.bulk}
                />
                <LinkItem
                  translationKey="menu.bulkManagement"
                  to={"approvals/bulk/approved-reject"}
                  icon={AppIcons.bulkManagement}
                  abacPath={NAV_ABAC.bulkApprovals}
                  roleCondition={"admin,tenant"}
                  customValidation={someTrue(navOptions, "bulk_act,bulk_mgmt")}
                  activeMatch={ACTIVE.bulk}
                />
                <LinkItem
                  translationKey="menu.senderIdManagement"
                  to={"approvals/sender/approved-reject"}
                  icon={AppIcons.senderIdManagement}
                  abacPath={NAV_ABAC.senderApprovals}
                  roleCondition={"superadmin,system"}
                  activeMatch={ACTIVE.senderId}
                />
                <LinkItem
                  translationKey="menu.senderIdManagement"
                  to={"approvals/sender/approved-reject"}
                  icon={AppIcons.senderIdManagement}
                  abacPath={NAV_ABAC.senderApprovals}
                  roleCondition={"admin,tenant"}
                  customValidation={someTrue(navOptions, "sender_act,sender_mgmt")}
                  activeMatch={ACTIVE.senderId}
                />
              </MenuSection>

              <MenuSection
                titleKey="sections.analyticsReports"
                sectionId="analyticsReports"
                icon={AppIcons.sectionAnalyticsReports}
              >
                <LinkItem translationKey="reports.campaign" toggle={"campaign"} abacPath={NAV_ABAC.reportCampaign} icon={AppIcons.campaign} navOption={"report"}>
                  <LinkItem translationKey="reports.aborted" to={"reports/campaign/aborted"} abacPath={rv("campaign", "aborted")} icon={AppIcons.aborted} />
                  <LinkItem translationKey="reports.campaign" to={"reports/campaign/campaign"} abacPath={rv("campaign", "campaign")} icon={AppIcons.campaignItem} />
                  <LinkItem translationKey="reports.schedule" to={"reports/campaign/schedule"} abacPath={rv("campaign", "schedule")} icon={AppIcons.scheduleManager} />
                </LinkItem>
                <LinkItem translationKey="reports.summary" toggle={"summary"} abacPath={NAV_ABAC.reportSummary} icon={AppIcons.summary} navOption={"report"}>
                  <LinkItem translationKey="reports.delivery" to={"reports/summary/delivery"} abacPath={rv("summary", "delivery")} icon={AppIcons.delivery} />
                  <LinkItem translationKey="reports.batch" to={"reports/summary/batch"} abacPath={rv("summary", "batch")} icon={AppIcons.batch} />
                </LinkItem>
                <LinkItem translationKey="reports.detailedAnalytics" toggle={"detailed"} abacPath={NAV_ABAC.reportDetailed} icon={AppIcons.detailedAnalytics} navOption={"report"}>
                  <LinkItem translationKey="reports.deliveryAdvance" to={"reports/detailed/advanced"} abacPath={rv("detailed_analytics", "delivery_advance")} icon={AppIcons.deliveryAdvance} />
                  <LinkItem translationKey="reports.deliveryContent" to={"reports/detailed/content"} abacPath={rv("detailed_analytics", "delivery_content")} icon={AppIcons.deliveryContent} />
                  <LinkItem
                    translationKey="reports.deliverySmsc"
                    to={"reports/detailed/smsc"}
                    abacPath={rv("detailed_analytics", "delivery_smsc")}
                    icon={AppIcons.delivery}
                    roleCondition={"admin,tenant"}
                    customValidation={
                      checkUserRole(user, "tenant") || shouldShowDlvSmsc
                    }
                  />
                </LinkItem>
                <LinkItem translationKey="reports.deliveryLatency" to={"reports/latency"} abacPath={rv("delivery_latency")} icon={AppIcons.deliveryLatency} navOption={"report"} />
                <LinkItem translationKey="reports.consumption" to={"reports/consumption"} abacPath={rv("consumption")} icon={AppIcons.consumption} navOption={"report"} />
                <LinkItem translationKey="reports.lookup" to={"reports/lookup"} abacPath={rv("lookup")} icon={AppIcons.lookup} navOption={"report"} />
                <LinkItem translationKey="reports.aggregator" to={"reports/aggregated"} abacPath={rv("aggregator")} icon={AppIcons.aggregator} roleCondition={"superadmin,admin,tenant,system"} navOption={"report"} />
                <LinkItem translationKey="reports.profit" to={"reports/profit"} abacPath={rv("profit")} icon={AppIcons.profit} roleCondition={"superadmin,admin,tenant,system"} navOption={"report"} />
                <LinkItem translationKey="reports.performance" toggle={"performance"} abacPath={NAV_ABAC.reportPerformance} icon={AppIcons.performance} roleCondition={"superadmin,system,tenant"} navOption={"report"}>
                  <LinkItem translationKey="menu.user" to={"reports/performance/user"} abacPath={rv("performance", "user")} icon={AppIcons.user} />
                  <LinkItem translationKey="reports.route" to={"reports/performance/route"} abacPath={rv("performance", "route")} icon={AppIcons.route} />
                </LinkItem>
                <LinkItem translationKey="reports.blocked" to={"reports/blocked"} abacPath={rv("blocked")} icon={AppIcons.blocked} roleCondition={"superadmin,system"} navOption={"report"} />
                <LinkItem translationKey="reports.tps" to={"reports/tps"} abacPath={rv("tps")} icon={AppIcons.tps} roleCondition={"admin,tenant,system,superadmin"} navOption={"report"} />
                <LinkItem translationKey="reports.vendorSummary" to={"reports/vendor-summary"} abacPath={rv("vendor_summary")} icon={AppIcons.vendorSummary} roleCondition={"admin,tenant,system,superadmin"} navOption={"report"} />
                <LinkItem translationKey="reports.edr" to={"reports/edr"} abacPath={rv("edr")} icon={AppIcons.edr} roleCondition={"admin,tenant,system,superadmin"} navOption={"report"} />
                <LinkItem translationKey="reports.coverage" to={"reports/coverage-report"} abacPath={rv("coverage")} icon={AppIcons.coverage} roleCondition={"superadmin,system,tenant"} navOption={"report"} />
                <LinkItem translationKey="reports.coverage" to={"reports/coverage-report"} abacPath={rv("coverage")} icon={AppIcons.coverage} roleCondition={"admin"} customValidation={!shouldShowDlvSmsc && !checkUserRole(user, "tenant")} navOption={"report"} />
              </MenuSection>

              <MenuSection
                titleKey="sections.complianceSecurity"
                sectionId="complianceSecurity"
                icon={AppIcons.sectionComplianceSecurity}
              >
                <LinkItem translationKey="menu.firewall" to={"firewall"} abacPath={NAV_ABAC.firewall} icon={AppIcons.firewall} roleCondition={"admin,tenant,system,superadmin"} customValidation={shouldEnforce ? canViewFirewall : Boolean(navOptions?.bsfm)} />
                <LinkItem translationKey="menu.networks" to={"network"} icon={AppIcons.networks} navOption={"sales"} roleCondition={"superadmin,system"} />
                <LinkItem translationKey="menu.pcap" to={"utilities/pcaps"} roleCondition={"superadmin,system,tenant"} icon={AppIcons.packetCapture} />
              </MenuSection>

              <MenuSection
                titleKey="sections.billingRouting"
                sectionId="billingRouting"
                icon={AppIcons.sectionBillingPricing}
              >
                <LinkItem translationKey="menu.routing" to={routingLandingPath} icon={AppIcons.routing} roleCondition={"superadmin,system,admin,tenant"} abacPath={NAV_ABAC.routing} navOption={"routing"} activeMatch={ACTIVE.routing} />
                <LinkItem translationKey="menu.senderBasedRouting" to={"routing/sender/add"} icon={AppIcons.senderBasedRouting} roleCondition={"superadmin,system"} abacPath={NAV_ABAC.routingSenderAdd} activeMatch={ACTIVE.senderRouting} />
              </MenuSection>

              <MenuSection
                titleKey="sections.developers"
                sectionId="developers"
                icon={AppIcons.sectionDeveloperTools}
              >
                <LinkItem
                  translationKey="menu.errorCodes"
                  to={"user/error-code"}
                  icon={AppIcons.errorCodes}
                  abacPath={hasRoles("user", user) ? undefined : NAV_ABAC.errorCode}
                  activeMatch={ACTIVE.errorCodes}
                />
                <LinkItem translationKey="menu.encodingTools" to={"utilities/converter"} icon={AppIcons.encodingTools} abacPath={NAV_ABAC.characterConverter} />
                <LinkItem translationKey="menu.sdks" to={"developer/sdk"} icon={AppIcons.sdk} roleCondition={"admin,user,superadmin,system,seller,manager,tenant"} customValidation={canViewSdkDocuments} />
                <LinkItem translationKey="menu.documents" to={"developer/documentation"} icon={AppIcons.documentation} roleCondition={"admin,user,superadmin,system,seller,manager,tenant"} customValidation={canViewPlatformDocuments} />
              </MenuSection>

              {isMenuSearching && !hasSearchHits && (
                <p className="sidebar-search-empty" role="status">
                  {t("search.noResults", {
                    defaultValue: "Can't find matching menu items",
                  })}
                </p>
              )}
            </div>
          ) : (
            <div className="links sidebar-nav" ref={sidebarLinksRef}>
              <SidebarActivePill pill={pill} />

              <MenuSection
                titleKey="sections.organizationAccounts"
                sectionId="sellerOrg"
                icon={AppIcons.sectionOrganizationAccounts}
                defaultOpen
              >
                <LinkItem
                  translationKey="menu.organizations"
                  to={"company"}
                  icon={AppIcons.organizations}
                />
                <LinkItem translationKey="menu.resellers" to={"sales-list"} icon={AppIcons.resellers} roleCondition={"manager"} />
                <LinkItem translationKey="seller.user" to={"reports/user-status"} icon={AppIcons.user} />
              </MenuSection>

              <MenuSection
                titleKey="sections.analyticsReports"
                sectionId="sellerReports"
                icon={AppIcons.sectionAnalyticsReports}
              >
                <LinkItem translationKey="seller.batch" to={"reports/batch-summary"} icon={AppIcons.batch} />
                <LinkItem translationKey="seller.submission" to={"reports/submission"} icon={AppIcons.submission} />
                <LinkItem translationKey="reports.delivery" to={"reports/delivery-advance"} icon={AppIcons.deliveryAdvance} />
                <LinkItem translationKey="reports.coverage" to={"reports/coverage"} icon={AppIcons.coverage} />
                <LinkItem translationKey="seller.performanceUser" to={"reports/performance-user"} icon={AppIcons.user} />
                <LinkItem translationKey="seller.transactionHistory" to={"reports/transaction-history"} icon={AppIcons.transactionHistory} />
              </MenuSection>

              <MenuSection
                titleKey="sections.customerEngagement"
                sectionId="sellerEngagement"
                icon={AppIcons.sectionEngagement}
              >
                <LinkItem translationKey="seller.view" toggle={"view"} icon={AppIcons.dltCompliance}>
                  <LinkItem translationKey="reports.schedule" icon={AppIcons.schedule} to={"view/schedule"} />
                  <LinkItem translationKey="menu.bulk" icon={AppIcons.bulkManagement} to={"view/bulk"} />
                </LinkItem>
              </MenuSection>

              <MenuSection
                titleKey="sections.developers"
                sectionId="sellerDevelopers"
                icon={AppIcons.sectionDeveloperTools}
              >
                <LinkItem translationKey="menu.sdks" to={"developer/sdk"} icon={AppIcons.sdk} customValidation={canViewSdkDocuments} />
                <LinkItem translationKey="menu.documents" to={"developer/documentation"} icon={AppIcons.documentation} customValidation={canViewPlatformDocuments} />
              </MenuSection>

              {isMenuSearching && !hasSearchHits && (
                <p className="sidebar-search-empty" role="status">
                  {t("search.noResults", {
                    defaultValue: "Can't find matching menu items",
                  })}
                </p>
              )}
            </div>
          )}
          <div className="logout">
            <p
              onClick={logout}
              {...getCollapsedTooltipProps(t("menu.logout"), isSidebarCollapsed)}
            >
              <img
                src={AppIcons.logout}
                alt=""
                loading="lazy"
                className="sidebar-link-icon"
              />
              <span className="link-title w-100">{t("menu.logout")}</span>
            </p>
          </div>
        </div>
      </div>
      <div className="mobToggle" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={t("tooltips.toggleSidebar", {
            defaultValue: "Toggle Sidebar",
          })}
          data-tooltip={t("tooltips.toggleSidebar")}
          data-tooltip-location="right"
        >
          <img src={AppIcons.mobToggle} alt="" loading="lazy" />
        </button>
      </div>
    </SidebarContext.Provider>
  );
};

export default Sidebar;
